/**
 * HistoricalTrendsTab — side-by-side trailing-N-period table.
 *
 * Each column is one period (W/M/Q/H/Y/D depending on global alignment),
 * each row is one metric. The rows are grouped into user-selectable
 * "sections" (a placement scope): the Totals — Search & Detail (& Homepage),
 * Search Total, Detail Total, Homepage, Others — plus any individual placement the
 * user adds (e.g. Native TOP · P1, Widget Bottom · P1 for SP; Sparkle · P1 for
 * SB). Each selected section renders as its own block of the metric rows over
 * the periods, and the placement selection governs all four sub-views
 * (Business Metrics / Metric Changes / Revenue Bridge / Growth Bridge).
 *
 * Reuses scale/format from reportGenerator and currency filtering rules
 * from SPBReportContent.
 */

import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  ListItemText,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { DashboardFilters } from "../../types";
import {
  determineScale,
  convertScaleToSuffix,
  formatNumber,
  getMetricsOrder,
} from "../../utils/reportGenerator";
import {
  useHistoricalTrendsData,
  type PeriodMetrics,
} from "./useHistoricalTrendsData";
import type { ReportData, TableData } from "../../utils/reportGenerator";
import {
  exportHistoricalTrendsToCsv,
  type TrendsCsvRow,
} from "../../utils/csvExport";

type TrendView = "actuals" | "changes" | "bridge" | "growth";

const VIEW_TABS: Array<{ value: TrendView; label: string }> = [
  { value: "actuals", label: "Business Metrics" },
  { value: "changes", label: "Metric Changes" },
  { value: "bridge", label: "Revenue Bridge" },
  { value: "growth", label: "Growth Bridge" },
];

/**
 * Sub-metric labels that should be visually indented under their parent
 * row (mirrors the same set used by the main dashboard tabs).
 */
const INDENTED_LABELS = new Set([
  "Coverage",
  "CPC",
  "CPC USD",
  "CPC LOC",
  "Ad Count",
  "Ad CTR",
  "Video Revenue USD",
  "Static Revenue USD",
  "Video Revenue LOC",
  "Static Revenue LOC",
]);

function isIndentedRow(label: string): boolean {
  // Strip optional scale suffix like " (MM)" / " (B)" before lookup.
  const base = label.replace(/\s*\([^)]*\)\s*$/, "").trim();
  return INDENTED_LABELS.has(base);
}

// Metric keys whose values get scaled (and so deserve a "(MM)"-style
// suffix on the row label rather than every cell).
const SCALED_METRIC_KEYS = new Set([
  "revenue_usd",
  "revenue_loc",
  "total_page_requests",
  "traffic",
  "video_revenue_usd",
  "static_revenue_usd",
  "video_revenue_loc",
  "static_revenue_loc",
]);

const USD_ROW_LABELS = new Set([
  "Revenue USD",
  "Video Revenue USD",
  "Static Revenue USD",
  "RPOM USD",
  "CPC USD",
]);

const LOC_ROW_LABELS = new Set([
  "Revenue LOC",
  "Video Revenue LOC",
  "Static Revenue LOC",
  "RPOM LOC",
  "CPC LOC",
]);

/** Column metric keys exposed by getMetricsOrder. */
type MetricKey = string;

interface RowDef {
  metricKey: MetricKey;
  label: string;
}

/**
 * Format a single cell value. The scale suffix (MM/B/K) is intentionally
 * dropped here; we surface it once on the row label instead so each cell
 * stays readable.
 */
/** Map MetricKey → numeric value from a PeriodMetrics. Used for the P1 / P2+
 *  aggregate sections in the Business Metrics and Metric Changes views, where
 *  the rolled-up Lambda-provided values (`p1_revenue_usd`, `p1_coverage`, etc.)
 *  are the correct source — placement cells in the main `metricsTable` /
 *  `changesTable` aren't summable for rate-style metrics. */
function pluck(m: PeriodMetrics, key: MetricKey): number | null {
  switch (key) {
    case "revenue_usd":
      return m.revenue_usd;
    case "revenue_loc":
      return m.revenue_loc;
    case "total_page_requests":
    case "traffic":
      return m.total_page_requests ?? null;
    case "rpom":
      return m.rpom;
    case "rpom_loc":
      return m.rpom_loc;
    case "coverage":
      return m.coverage;
    case "cpc":
      return m.cpc;
    case "cpc_loc":
      return m.cpc_loc;
    case "ad_count":
      return m.ad_count;
    case "ad_ctr":
      return m.ad_ctr;
    case "video_revenue_usd":
      return m.video_revenue_usd ?? null;
    case "static_revenue_usd":
      return m.static_revenue_usd ?? null;
    case "video_revenue_loc":
      return m.video_revenue_loc ?? null;
    case "static_revenue_loc":
      return m.static_revenue_loc ?? null;
    default:
      return null;
  }
}

function fmtCell(
  value: number | null,
  metricKey: MetricKey,
  scale: number
): string {
  if (value == null) return "—";
  const useScale = SCALED_METRIC_KEYS.has(metricKey);
  const [body] = formatNumber(value, metricKey, useScale ? scale : undefined);
  return body;
}

/** Format a signed scaled value (used by bridge cells). */
function fmtSignedScaled(
  value: number | null,
  metricKey: MetricKey,
  scale: number
): string {
  if (value == null) return "—";
  const formatted = fmtCell(Math.abs(value), metricKey, scale);
  const stripped = formatted.replace(/^\$/, "");
  const usesDollar = formatted.startsWith("$");
  const sign = value >= 0 ? "+" : "-";
  return usesDollar ? `${sign}$${stripped}` : `${sign}${stripped}`;
}

type CellSign = "positive" | "negative" | "neutral";
interface ViewCell {
  value: string;
  sign: CellSign;
}

function signOf(n: number | null | undefined): CellSign {
  if (n == null || n === 0) return "neutral";
  return n > 0 ? "positive" : "negative";
}

/**
 * Cell-index selector within a `ReportData` table row. Mirrors the dimOrder
 * definition in reportGenerator.ts. A range `[from, to]` is summed (inclusive
 * of `from`, exclusive of `to`); a single index `n` is taken directly. Hidden
 * dim entries (Page1 / Page2 rollups) are NOT in cells, so for the P1 / P2+
 * aggregate sections we sum the visible placement cells instead.
 */
interface SegmentCols {
  /** Single column index (a Total or an individual placement). */
  index?: number;
  /** Range of placement columns to sum (used for the P1 / P2+ aggregates). */
  range?: [number, number];
}

/** Dropdown groups for the placement selector. */
type SectionGroup =
  | "Totals"
  | "Search · Page 1"
  | "Search · Page 2+"
  | "Detail";

const SECTION_GROUP_ORDER: SectionGroup[] = [
  "Totals",
  "Search · Page 1",
  "Search · Page 2+",
  "Detail",
];

/**
 * A selectable placement section. Each renders as a block of the metric rows
 * over the periods. `cols` locates the value inside each period's ReportData
 * table (see SegmentCols). `rolled` marks the P1 / P2+ *aggregate* sections:
 * their rate-style metrics aren't summable across placements, so the actuals /
 * changes views read the Lambda-provided rolled values instead of summing
 * placement cells. Individual-placement sections need no rolled handling —
 * their ReportData cell already holds the correct per-placement value.
 */
interface SectionSpec {
  id: string;
  label: string;
  group: SectionGroup;
  cols: SegmentCols;
  rolled?: "p1" | "p2";
  defaultSelected?: boolean;
}

// Column indices below mirror getDimOrder("SP") / ("SB") in reportGenerator.ts,
// counting only "show" dims in cell order (Page1 / Page2 rollups are hidden).
const SECTIONS_SP: SectionSpec[] = [
  // Totals (0: S+D+H, 1: Total Search, 12: Total Detail, 18: Homepage,
  // 19: Others)
  {
    id: "headline",
    label: "Search & Detail & Homepage",
    group: "Totals",
    cols: { index: 0 },
    defaultSelected: true,
  },
  {
    id: "search-total",
    label: "Search — Total",
    group: "Totals",
    cols: { index: 1 },
    defaultSelected: true,
  },
  {
    id: "search-p1-all",
    label: "Search — Page 1 (all)",
    group: "Totals",
    cols: { range: [2, 7] },
    rolled: "p1",
  },
  {
    id: "search-p2-all",
    label: "Search — Page 2+ (all)",
    group: "Totals",
    cols: { range: [7, 12] },
    rolled: "p2",
  },
  {
    id: "detail-total",
    label: "Detail — Total",
    group: "Totals",
    cols: { index: 12 },
    defaultSelected: true,
  },
  {
    id: "homepage",
    label: "Homepage",
    group: "Totals",
    cols: { index: 18 },
    defaultSelected: true,
  },
  {
    // All other on-site categories; outside the Search & Detail & Homepage total.
    id: "others",
    label: "Others",
    group: "Totals",
    cols: { index: 19 },
    defaultSelected: true,
  },
  // Search · Page 1 (cols 2..6)
  {
    id: "sp1-top",
    label: "Native TOP",
    group: "Search · Page 1",
    cols: { index: 2 },
  },
  {
    id: "sp1-mop",
    label: "Native MOP",
    group: "Search · Page 1",
    cols: { index: 3 },
  },
  {
    id: "sp1-bop",
    label: "Native BOP",
    group: "Search · Page 1",
    cols: { index: 4 },
  },
  {
    id: "sp1-inline",
    label: "Widget Inline",
    group: "Search · Page 1",
    cols: { index: 5 },
  },
  {
    id: "sp1-bottom",
    label: "Widget Bottom",
    group: "Search · Page 1",
    cols: { index: 6 },
  },
  // Search · Page 2+ (cols 7..11)
  {
    id: "sp2-top",
    label: "Native TOP",
    group: "Search · Page 2+",
    cols: { index: 7 },
  },
  {
    id: "sp2-mop",
    label: "Native MOP",
    group: "Search · Page 2+",
    cols: { index: 8 },
  },
  {
    id: "sp2-bop",
    label: "Native BOP",
    group: "Search · Page 2+",
    cols: { index: 9 },
  },
  {
    id: "sp2-inline",
    label: "Widget Inline",
    group: "Search · Page 2+",
    cols: { index: 10 },
  },
  {
    id: "sp2-bottom",
    label: "Widget Bottom",
    group: "Search · Page 2+",
    cols: { index: 11 },
  },
  // Detail (cols 13..17)
  { id: "dp1", label: "DP1", group: "Detail", cols: { index: 13 } },
  { id: "dpt", label: "DP Thematic", group: "Detail", cols: { index: 14 } },
  { id: "dp2", label: "DP2", group: "Detail", cols: { index: 15 } },
  { id: "hqp", label: "HQP", group: "Detail", cols: { index: 16 } },
  { id: "ilm", label: "ILM", group: "Detail", cols: { index: 17 } },
];

const SECTIONS_SB: SectionSpec[] = [
  // Totals (0: S+D, 1: Total Search, 2: Total Sparkle, 11: Total Detail)
  {
    id: "headline",
    label: "Search & Detail",
    group: "Totals",
    cols: { index: 0 },
    defaultSelected: true,
  },
  {
    id: "search-total",
    label: "Search — Total",
    group: "Totals",
    cols: { index: 1 },
    defaultSelected: true,
  },
  {
    id: "search-sparkle-total",
    label: "Search — Total Sparkle",
    group: "Totals",
    cols: { index: 2 },
  },
  {
    id: "search-p1-all",
    label: "Search — Page 1 (all)",
    group: "Totals",
    cols: { range: [3, 7] },
    rolled: "p1",
  },
  {
    id: "search-p2-all",
    label: "Search — Page 2+ (all)",
    group: "Totals",
    cols: { range: [7, 11] },
    rolled: "p2",
  },
  {
    id: "detail-total",
    label: "Detail — Total",
    group: "Totals",
    cols: { index: 11 },
    defaultSelected: true,
  },
  // Search · Page 1 (cols 3..6)
  {
    id: "sb-sp1-sparkle",
    label: "Sparkle",
    group: "Search · Page 1",
    cols: { index: 3 },
  },
  {
    id: "sb-sp1-inline",
    label: "Inline",
    group: "Search · Page 1",
    cols: { index: 4 },
  },
  {
    id: "sb-sp1-bottom",
    label: "Bottom",
    group: "Search · Page 1",
    cols: { index: 5 },
  },
  {
    id: "sb-sp1-3psl",
    label: "3PSL",
    group: "Search · Page 1",
    cols: { index: 6 },
  },
  // Search · Page 2+ (cols 7..10)
  {
    id: "sb-sp2-sparkle",
    label: "Sparkle",
    group: "Search · Page 2+",
    cols: { index: 7 },
  },
  {
    id: "sb-sp2-inline",
    label: "Inline",
    group: "Search · Page 2+",
    cols: { index: 8 },
  },
  {
    id: "sb-sp2-bottom",
    label: "Bottom",
    group: "Search · Page 2+",
    cols: { index: 9 },
  },
  {
    id: "sb-sp2-3psl",
    label: "3PSL",
    group: "Search · Page 2+",
    cols: { index: 10 },
  },
  // Detail (cols 12..15)
  {
    id: "sb-brand-zone",
    label: "Brand Zone",
    group: "Detail",
    cols: { index: 12 },
  },
  { id: "sb-ilm", label: "ILM", group: "Detail", cols: { index: 13 } },
  { id: "sb-hqp", label: "HQP", group: "Detail", cols: { index: 14 } },
  {
    id: "sb-other-dp",
    label: "Other DP",
    group: "Detail",
    cols: { index: 15 },
  },
];

function sectionsForProduct(product: "SP" | "SB"): SectionSpec[] {
  return product === "SP" ? SECTIONS_SP : SECTIONS_SB;
}

function defaultSelectedIds(product: "SP" | "SB"): string[] {
  return sectionsForProduct(product)
    .filter((s) => s.defaultSelected)
    .map((s) => s.id);
}

/**
 * Disambiguated label for a section, used on the table block header, chips and
 * CSV. Individual Search placements share a name across pages (e.g. "Native
 * TOP" exists on both Page 1 and Page 2+), so append the page here; the
 * dropdown itself relies on its group subheader instead.
 */
function sectionDisplayLabel(s: SectionSpec): string {
  if (s.group === "Search · Page 1") return `${s.label} · Page 1`;
  if (s.group === "Search · Page 2+") return `${s.label} · Page 2+`;
  return s.label;
}

/**
 * Map metricKey (from the trends row list) to the row label used in the main
 * dashboard's `metricsTable` / `bridgeTable` etc. Lookup by row label is
 * how we find the right row inside a TableData.
 */
const METRIC_KEY_TO_LABEL_BASE: Record<string, string> = {
  revenue_usd: "Revenue USD",
  revenue_loc: "Revenue LOC",
  total_page_requests: "Page Views",
  traffic: "Page Views",
  rpom: "RPOM USD",
  rpom_loc: "RPOM LOC",
  coverage: "Coverage",
  cpc: "CPC USD",
  cpc_loc: "CPC LOC",
  ad_count: "Ad Count",
  ad_ctr: "Ad CTR",
  video_revenue_usd: "Video Revenue USD",
  static_revenue_usd: "Static Revenue USD",
  video_revenue_loc: "Video Revenue LOC",
  static_revenue_loc: "Static Revenue LOC",
};

/** Find the row in a TableData whose label (with optional " (MM)" / " (B)"
 *  suffix stripped) matches the metric key's base label. */
function findRow(
  table: TableData | undefined,
  metricKey: MetricKey
): { value: string; rawValue: number | null; cssClass: string }[] | null {
  if (!table) return null;
  const base = METRIC_KEY_TO_LABEL_BASE[metricKey];
  if (!base) return null;
  const row = table.rows.find((r) => {
    const lbl = r.label.replace(/\s*\([^)]*\)\s*$/, "").trim();
    return lbl === base;
  });
  return row ? row.cells : null;
}

/** Extract a cell (or sum a range of cells) from a ReportData table for a
 *  given column selector + metric. Returns `{ formatted, raw }` so the caller
 *  can decide whether to apply color (and detect "neutral" actuals). */
function extractFromTable(
  table: TableData | undefined,
  cols: SegmentCols,
  metricKey: MetricKey
): { formatted: string | null; raw: number | null } {
  const cells = findRow(table, metricKey);
  if (!cells) return { formatted: null, raw: null };
  if (cols.index != null) {
    if (cols.index < 0 || cols.index >= cells.length)
      return { formatted: null, raw: null };
    const c = cells[cols.index];
    return { formatted: c.value, raw: c.rawValue };
  }
  if (cols.range) {
    const [from, to] = cols.range;
    let sum = 0;
    let any = false;
    for (let i = from; i < to && i < cells.length; i++) {
      const v = cells[i].rawValue;
      if (v != null) {
        sum += v;
        any = true;
      }
    }
    if (!any) return { formatted: null, raw: null };
    return { formatted: null, raw: sum };
  }
  return { formatted: null, raw: null };
}

/** Pick the correct table from a ReportData based on the selected sub-tab. */
function tableForView(
  reportData: ReportData,
  view: TrendView
): TableData | undefined {
  switch (view) {
    case "actuals":
      return reportData.metricsTable;
    case "changes":
      return reportData.changesTable;
    case "bridge":
      return reportData.bridgeTable;
    case "growth":
      return reportData.growthTable;
  }
}

/** Sign-format a signed scaled $ value (Bridge view rendering). */
function fmtBridgeFromRaw(raw: number | null, scale: number): string {
  if (raw == null) return "—";
  return fmtSignedScaled(raw, "revenue_usd", scale);
}

/** Render a cell from a `ReportData` table for a given (section, metric, view).
 *  Whenever possible reads the cell value the main dashboard already
 *  formatted; for the P1 / P2+ aggregates which aren't surfaced as cells, we
 *  sum the visible placement cells. Coloring matches the main tab.
 *
 *  `useRolled` is set only for the P1 / P2+ aggregate sections on the
 *  actuals / changes views, where placement-cell summing produces nonsense
 *  (cells contain rate-style metrics that aren't summable across placements).
 *  Bridge / growth views correctly sum placement cells because those cells
 *  hold additive contribution amounts, and individual placements always read
 *  their own cell directly. */
function cellFromReport(
  view: TrendView,
  cols: SegmentCols,
  useRolled: boolean,
  reportData: ReportData,
  metricKey: MetricKey,
  scale: number,
  rolledTarget?: PeriodMetrics,
  rolledBaseline?: PeriodMetrics
): ViewCell {
  if (useRolled && rolledTarget && rolledBaseline) {
    const tv = pluck(rolledTarget, metricKey);
    const bv = pluck(rolledBaseline, metricKey);
    if (view === "actuals") {
      return { value: fmtCell(tv, metricKey, scale), sign: "neutral" };
    }
    // changes: % change with same one-decimal format the main tab uses
    if (tv == null || bv == null || bv === 0)
      return { value: "—", sign: "neutral" };
    const pct = ((tv - bv) / bv) * 100;
    const sign = pct > 0 ? "+" : "";
    return {
      value: `${sign}${pct.toFixed(1)}%`,
      sign: signOf(pct),
    };
  }

  const table = tableForView(reportData, view);
  const { formatted, raw } = extractFromTable(table, cols, metricKey);

  // For Total / individual-placement cells we typically have a pre-formatted
  // string from the main tab; for the P1 / P2+ aggregates on bridge/growth we
  // have a summed raw number that IS mathematically correct (placement bridge
  // contributions are additive).
  //
  // Scaled metrics (Revenue, Page Views) need consistent units across all
  // sections within a single row. The main tab formats Total cells with its
  // own scale (computed from total magnitude), while the aggregates use the
  // trends `sharedScale`. To keep every section in the same units, re-format
  // scaled rows from `raw` using `sharedScale` instead of the pre-formatted
  // main-tab string. Non-scaled rows still use the main-tab string (it carries
  // native units like %, $/click, etc.).
  const isScaled = SCALED_METRIC_KEYS.has(metricKey);

  switch (view) {
    case "actuals": {
      if (isScaled) {
        return { value: fmtCell(raw, metricKey, scale), sign: "neutral" };
      }
      if (formatted != null) return { value: formatted, sign: "neutral" };
      return { value: fmtCell(raw, metricKey, scale), sign: "neutral" };
    }

    case "changes": {
      if (formatted != null) {
        return { value: formatted, sign: signOf(raw) };
      }
      return { value: "—", sign: "neutral" };
    }

    case "bridge": {
      // Bridge values are dollar contributions — always scaled. Re-format
      // every cell from raw using sharedScale so units line up with the row
      // label suffix.
      return {
        value: fmtBridgeFromRaw(raw, scale),
        sign: signOf(raw),
      };
    }

    case "growth": {
      if (formatted != null) {
        return { value: formatted, sign: signOf(raw) };
      }
      if (raw == null) return { value: "—", sign: "neutral" };
      const pct = raw * 100;
      const sign = pct > 0 ? "+" : "";
      return {
        value: `${sign}${pct.toFixed(1)}%`,
        sign: signOf(raw),
      };
    }
  }
}

interface Props {
  filters: Partial<DashboardFilters>;
  product: "SP" | "SB";
  currency: "USD" | "LOC";
  trailingCount?: number;
  /** Optional CSV exporter registration (provided by DashboardShell via
   *  SPBReportContent). Registered when this tab mounts, cleared on
   *  unmount, so the global download button targets whichever sub-tab
   *  view is currently displayed. */
  registerCsvExporter?: (
    exporter: (() => { csv: string; filename: string }) | null
  ) => void;
}

// Default trailing periods per alignment (what loads before the user chooses).
const TRAILING_COUNT_BY_ALIGNMENT: Record<string, number> = {
  D: 14,
  W: 10,
  M: 12,
  Q: 5,
  H: 4,
  Y: 3,
};

// Upper bound the user can pick per alignment. Each trailing period is its own
// parallel Athena query, so these caps bound query fan-out / latency.
const TRAILING_MAX_BY_ALIGNMENT: Record<string, number> = {
  D: 90,
  W: 20,
  M: 24,
  Q: 10,
  H: 8,
  Y: 5,
};

// Counts above this many periods are flagged in the dropdown: each period is a
// separate Athena query (fetched through a bounded pool), so large selections
// take noticeably longer and produce a very wide table.
const TRAILING_SLOW_THRESHOLD = 30;

// Curated choices for alignments whose cap is too large to enumerate one value
// at a time (D goes to 90). Alignments absent from this map fall back to every
// value from 2..cap, which stays a comfortable menu at those caps (<= 24).
const TRAILING_PRESETS_BY_ALIGNMENT: Record<string, number[]> = {
  D: [7, 14, 30, 60, 90],
};

/**
 * The selectable trailing-period counts for an alignment: its presets when
 * defined, otherwise every value from 2 up to the cap. `current` is folded in
 * so a value supplied from outside (or a stale pick) always matches an option
 * instead of rendering an empty Select.
 */
export function trailingOptionsFor(
  alignment: string,
  max: number,
  current?: number
): number[] {
  const base =
    TRAILING_PRESETS_BY_ALIGNMENT[alignment]?.filter((n) => n <= max) ??
    Array.from({ length: Math.max(0, max - 1) }, (_, i) => i + 2);
  const withCurrent =
    current != null && current >= 2 && current <= max && !base.includes(current)
      ? [...base, current]
      : base;
  return Array.from(new Set(withCurrent)).sort((a, b) => a - b);
}

const HistoricalTrendsTab: React.FC<Props> = ({
  filters,
  product,
  currency,
  trailingCount,
  registerCsvExporter,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [view, setView] = useState<TrendView>("actuals");

  // "Freeze panes": the table scrolls inside its own box so the date header
  // stays pinned at the top, metric labels stay pinned on the left, and the
  // current section's name pins just under the date header. The header's
  // height is measured so the section rows know where to stick.
  const headerRowRef = React.useRef<HTMLTableRowElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  React.useLayoutEffect(() => {
    const el = headerRowRef.current;
    if (!el) return;
    const measure = () => setHeaderHeight(el.offsetHeight);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  });

  // Which placement sections are shown (in catalog order). Defaults to the
  // Totals set (Search & Detail (& Homepage), Search Total, Detail Total,
  // Homepage). Resets when the product changes since the catalogs differ.
  const catalog = useMemo(() => sectionsForProduct(product), [product]);
  const [selectedIds, setSelectedIds] = useState<string[]>(() =>
    defaultSelectedIds(product)
  );
  React.useEffect(() => {
    setSelectedIds(defaultSelectedIds(product));
  }, [product]);
  const selectedSections = useMemo(
    () => catalog.filter((s) => selectedIds.includes(s.id)),
    [catalog, selectedIds]
  );

  // User-chosen trailing-period count. Null = use the alignment's default.
  // Reset whenever the alignment changes (the sensible count differs per
  // granularity, and a Day count could exceed a Year cap).
  const alignment = (filters.alignment as string) ?? "W";
  const [periodCount, setPeriodCount] = useState<number | null>(null);
  React.useEffect(() => {
    setPeriodCount(null);
  }, [alignment]);

  const defaultTrailingCount = TRAILING_COUNT_BY_ALIGNMENT[alignment] ?? 9;
  const maxTrailingCount =
    TRAILING_MAX_BY_ALIGNMENT[alignment] ?? defaultTrailingCount;
  const effectiveTrailingCount =
    trailingCount ??
    Math.min(periodCount ?? defaultTrailingCount, maxTrailingCount);

  const { periods, reports, isLoading, error, failedPeriods } =
    useHistoricalTrendsData(filters, effectiveTrailingCount);

  const rows: RowDef[] = useMemo(() => {
    const all = getMetricsOrder(product).map(([key, label]) => ({
      metricKey: key,
      label,
    }));
    const hidden = currency === "USD" ? LOC_ROW_LABELS : USD_ROW_LABELS;
    return all.filter((r) => {
      const base = r.label.replace(/\s*\([^)]*\)\s*$/, "").trim();
      return !hidden.has(base);
    });
  }, [product, currency]);

  // Compute a single shared scale for revenue rows so columns are comparable.
  const sharedScale = useMemo(() => {
    let max = 0;
    for (const p of periods) {
      const v = p.searchTotal.revenue_usd;
      if (v != null && Math.abs(v) > max) max = Math.abs(v);
    }
    return determineScale(max || 1);
  }, [periods]);

  const handlePlacementChange = (e: SelectChangeEvent<string[]>) => {
    const value = e.target.value;
    setSelectedIds(typeof value === "string" ? value.split(",") : value);
  };

  const removeSection = (id: string) =>
    setSelectedIds((ids) => ids.filter((x) => x !== id));

  // Register a tab-aware CSV exporter so the global download button writes
  // the currently-selected sub-tab view (Business Metrics / Metric Changes /
  // Revenue Bridge / Growth Bridge), respects currency, and includes exactly
  // the placement sections the user has selected.
  React.useEffect(() => {
    if (!registerCsvExporter) return;
    if (periods.length === 0 || reports.length === 0) {
      registerCsvExporter(null);
      return;
    }

    const buildCsv = () => {
      const periodLabels = periods.map((p) => p.label);
      const csvRows: TrendsCsvRow[] = [];

      const showScaleSuffix = view === "actuals" || view === "bridge";
      const scaleSuffixLocal = convertScaleToSuffix(sharedScale);

      for (const section of selectedSections) {
        const useRolled =
          !!section.rolled && (view === "actuals" || view === "changes");
        for (const r of rows) {
          const isScaledRow = SCALED_METRIC_KEYS.has(r.metricKey);
          const labelWithUnit =
            isScaledRow && scaleSuffixLocal && showScaleSuffix
              ? `${r.label} (${scaleSuffixLocal})`
              : r.label;

          const values = periods.map((p, pi) => {
            const periodReport = reports[pi];
            if (!periodReport) return "—";
            const rolledTarget =
              section.rolled === "p1"
                ? p.searchP1
                : section.rolled === "p2"
                ? p.searchP2
                : undefined;
            const rolledBaseline =
              section.rolled === "p1"
                ? p.baselineSearchP1
                : section.rolled === "p2"
                ? p.baselineSearchP2
                : undefined;
            const cell = cellFromReport(
              view,
              section.cols,
              useRolled,
              periodReport.reportData,
              r.metricKey,
              sharedScale,
              rolledTarget,
              rolledBaseline
            );
            return cell.value;
          });
          csvRows.push({
            category: sectionDisplayLabel(section),
            metric: labelWithUnit,
            segment: section.group,
            values,
          });
        }
      }

      const viewLabel =
        VIEW_TABS.find((t) => t.value === view)?.label ?? "Business Metrics";
      const alignmentLabel = (filters.alignment as string) ?? "W";
      const comparisonModeLabel = (filters.comparisonMode as string) ?? "YoY";
      const geoLabel = String(filters.geo ?? "ww").toUpperCase();
      const deviceLabel = String(filters.device ?? "Desktop+Mobile").replace(
        "-",
        "+"
      );
      const csv = exportHistoricalTrendsToCsv(
        viewLabel,
        product,
        geoLabel,
        deviceLabel,
        currency,
        alignmentLabel,
        comparisonModeLabel,
        periodLabels,
        csvRows
      );
      const tagSafe = viewLabel.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      const filename = `finn-historical-trends-${product.toLowerCase()}-${tagSafe}.csv`;
      return { csv, filename };
    };

    registerCsvExporter(buildCsv);
    return () => {
      registerCsvExporter(null);
    };
  }, [
    registerCsvExporter,
    periods,
    reports,
    rows,
    view,
    selectedSections,
    sharedScale,
    product,
    currency,
    filters.alignment,
    filters.comparisonMode,
    filters.geo,
    filters.device,
  ]);

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 1.5 }}>
        {error}
      </Alert>
    );
  }

  if (isLoading && periods.length === 0) {
    const periodsLabel = effectiveTrailingCount;
    const periodWord = periodWordFor(filters.alignment as string | undefined);
    return (
      <Box
        sx={{
          mt: 1.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          py: 8,
        }}
      >
        <CircularProgress size={32} thickness={4} />
        <Typography variant="body2" color="text.secondary">
          Loading {periodsLabel} {periodWord} of historical data…
        </Typography>
      </Box>
    );
  }

  if (!isLoading && periods.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 1.5 }}>
        Apply filters to load historical trends.
      </Alert>
    );
  }

  // Style tokens — kept local to the component to mirror ReportTable.
  const hdrDark = isDark ? "#1a3a6a" : "#1a5aa0";
  const hdrText = "#ffffff";
  const rowLabelBg = isDark ? "#151e2d" : "#f8f9fa";
  const cellBorder = isDark ? "rgba(255,255,255,0.08)" : "#dee2e6";
  const stripeBg = isDark ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.02)";
  const hoverBg = isDark ? "rgba(255,255,255,0.03)" : "rgba(44,120,212,0.05)";

  const scaleSuffix = convertScaleToSuffix(sharedScale);
  // Build the grouped placement menu (ListSubheader per group + a checkbox
  // MenuItem per section, in catalog order).
  const placementMenu: React.ReactNode[] = [];
  for (const group of SECTION_GROUP_ORDER) {
    const items = catalog.filter((s) => s.group === group);
    if (items.length === 0) continue;
    placementMenu.push(
      <ListSubheader key={`grp-${group}`} disableSticky>
        {group}
      </ListSubheader>
    );
    for (const s of items) {
      placementMenu.push(
        <MenuItem key={s.id} value={s.id} dense>
          <Checkbox size="small" checked={selectedIds.includes(s.id)} />
          <ListItemText primary={s.label} />
        </MenuItem>
      );
    }
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      {isLoading && periods.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
            px: 1.25,
            py: 0.75,
            borderRadius: 1,
            bgcolor: isDark ? "rgba(33,150,243,0.08)" : "rgba(33,150,243,0.06)",
            border: `1px solid ${
              isDark ? "rgba(33,150,243,0.25)" : "rgba(33,150,243,0.2)"
            }`,
          }}
        >
          <CircularProgress size={14} thickness={5} />
          <Typography variant="caption" color="text.secondary">
            Refreshing historical data…
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <LinearProgress sx={{ height: 3, borderRadius: 1 }} />
          </Box>
        </Box>
      )}
      <Tabs
        value={view}
        onChange={(_, v) => setView(v as TrendView)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 36,
          mb: 1.5,
          "& .MuiTab-root": {
            textTransform: "none",
            minHeight: 36,
            fontWeight: 600,
            fontSize: "0.8rem",
          },
        }}
      >
        {VIEW_TABS.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
          flexWrap: "wrap",
          fontSize: "0.85rem",
          color: theme.palette.text.secondary,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Trailing {periods.length} {periodWordFor(filters.alignment)} ending{" "}
          {periods[periods.length - 1]?.label ?? ""}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <FormControl size="small" sx={{ minWidth: 210 }}>
          <InputLabel id="ht-placement-label">Placements</InputLabel>
          <Select
            labelId="ht-placement-label"
            multiple
            value={selectedIds}
            onChange={handlePlacementChange}
            input={<OutlinedInput label="Placements" />}
            renderValue={(sel) => `${(sel as string[]).length} selected`}
            MenuProps={{ PaperProps: { style: { maxHeight: 440 } } }}
          >
            {placementMenu}
          </Select>
        </FormControl>
        {/* Trailing-period count — curated presets where the cap is large
            (Day goes to 90), otherwise every value from 2 up to the cap. */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="ht-periods-label">Trailing periods</InputLabel>
          <Select
            labelId="ht-periods-label"
            label="Trailing periods"
            value={String(effectiveTrailingCount)}
            onChange={(e) => setPeriodCount(Number(e.target.value))}
            MenuProps={{ PaperProps: { style: { maxHeight: 440 } } }}
          >
            {trailingOptionsFor(
              alignment,
              maxTrailingCount,
              effectiveTrailingCount
            ).map((count) => (
              <MenuItem key={count} value={String(count)}>
                {count} {periodWordFor(filters.alignment)}
                {count === defaultTrailingCount ? " (default)" : ""}
                {count > TRAILING_SLOW_THRESHOLD ? (
                  <Box
                    component="span"
                    sx={{
                      ml: 0.75,
                      color: "text.secondary",
                      fontSize: "0.8em",
                    }}
                  >
                    — slower
                  </Box>
                ) : null}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flexGrow: 3 }} />
      </Box>
      {failedPeriods.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.25 }}>
          Couldn&apos;t load data for {failedPeriods.length} period
          {failedPeriods.length === 1 ? "" : "s"} ({failedPeriods.join(", ")}) —
          those columns are blank. This is usually a transient query failure;
          re-apply the filters to retry, or choose fewer trailing periods.
        </Alert>
      )}
      {selectedSections.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.25 }}>
          {selectedSections.map((s) => (
            <Chip
              key={s.id}
              label={sectionDisplayLabel(s)}
              size="small"
              variant="outlined"
              onDelete={() => removeSection(s.id)}
            />
          ))}
        </Box>
      )}
      {selectedSections.length === 0 ? (
        <Alert severity="info" sx={{ mt: 0.5 }}>
          Select at least one placement section to display.
        </Alert>
      ) : (
        <Box
          sx={{
            overflow: "auto",
            maxHeight: "calc(100vh - 220px)",
            borderRadius: 1,
            "@media print": { maxHeight: "none", overflow: "visible" },
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              fontSize: "0.78rem",
              fontFamily: theme.typography.fontFamily,
            }}
          >
            <thead>
              {/* Single header row: Metric | W/E period 1 | W/E period 2 | ... */}
              <tr ref={headerRowRef}>
                <th
                  style={{
                    ...thStyle(hdrDark, hdrText, cellBorder),
                    minWidth: 200,
                    textAlign: "left",
                    paddingLeft: 14,
                    left: 0,
                    zIndex: 4,
                  }}
                >
                  Metric
                </th>
                {periods.map((p, i) => (
                  <th
                    key={`p-${i}`}
                    style={thStyle(hdrDark, hdrText, cellBorder)}
                  >
                    {p.label}
                  </th>
                ))}
              </tr>
            </thead>
            {/* One block per selected placement section: a bold section header
                row plus one row per metric, over the period columns. */}
            <tbody>
              {selectedSections.map((section) => {
                const numPeriodCols = periods.length;
                const useRolled =
                  !!section.rolled &&
                  (view === "actuals" || view === "changes");
                return (
                  <React.Fragment key={section.id}>
                    {/* Section header row — pins just under the date header while
                        its rows are on screen (a later section's header paints
                        over it), and its name stays visible when scrolling right. */}
                    <tr>
                      <td
                        style={{
                          padding: "8px 14px",
                          backgroundColor: rowLabelBg,
                          border: `1px solid ${cellBorder}`,
                          fontWeight: 700,
                          fontSize: "0.9rem",
                          color: theme.palette.text.primary,
                          whiteSpace: "nowrap",
                          position: "sticky",
                          left: 0,
                          top: headerHeight,
                          zIndex: 3,
                        }}
                      >
                        {sectionDisplayLabel(section)}
                      </td>
                      {numPeriodCols > 0 && (
                        <td
                          colSpan={numPeriodCols}
                          style={{
                            backgroundColor: rowLabelBg,
                            border: `1px solid ${cellBorder}`,
                            position: "sticky",
                            top: headerHeight,
                            zIndex: 2,
                          }}
                        />
                      )}
                    </tr>
                    {/* Metric rows */}
                    {rows.map((r, ri) => {
                      const showScaleSuffix =
                        view === "actuals" || view === "bridge";
                      const isScaled = SCALED_METRIC_KEYS.has(r.metricKey);
                      const labelWithUnit =
                        isScaled && scaleSuffix && showScaleSuffix
                          ? `${r.label} (${scaleSuffix})`
                          : r.label;
                      const bg = ri % 2 === 0 ? "transparent" : stripeBg;
                      const subIndent = isIndentedRow(labelWithUnit) ? 16 : 0;
                      return (
                        <tr
                          key={`${section.id}-${r.metricKey}`}
                          style={{ background: bg }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = hoverBg)
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = bg)
                          }
                        >
                          <td
                            style={{
                              padding: "6px 14px",
                              paddingLeft: 24 + subIndent,
                              backgroundColor: rowLabelBg,
                              border: `1px solid ${cellBorder}`,
                              whiteSpace: "nowrap",
                              fontWeight: 500,
                              color: theme.palette.text.primary,
                              position: "sticky",
                              left: 0,
                              zIndex: 1,
                            }}
                          >
                            {labelWithUnit}
                          </td>
                          {periods.map((p, pi) => {
                            const periodReport = reports[pi];
                            const rolledTarget =
                              section.rolled === "p1"
                                ? p.searchP1
                                : section.rolled === "p2"
                                ? p.searchP2
                                : undefined;
                            const rolledBaseline =
                              section.rolled === "p1"
                                ? p.baselineSearchP1
                                : section.rolled === "p2"
                                ? p.baselineSearchP2
                                : undefined;
                            const cell: ViewCell = periodReport
                              ? cellFromReport(
                                  view,
                                  section.cols,
                                  useRolled,
                                  periodReport.reportData,
                                  r.metricKey,
                                  sharedScale,
                                  rolledTarget,
                                  rolledBaseline
                                )
                              : { value: "—", sign: "neutral" };
                            return renderCell(
                              cell.value,
                              `${pi}`,
                              cellBorder,
                              false,
                              cell.sign,
                              `${sectionDisplayLabel(
                                section
                              )} · ${labelWithUnit} · ${p.label}`
                            );
                          })}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}
    </Box>
  );
};

function thStyle(
  bg: string,
  color: string,
  border: string
): React.CSSProperties {
  return {
    padding: "8px 10px",
    textAlign: "center",
    fontWeight: 600,
    fontSize: "0.72rem",
    whiteSpace: "nowrap",
    backgroundColor: bg,
    color,
    border: `1px solid ${border}`,
    position: "sticky",
    top: 0,
    zIndex: 3,
  };
}

function renderCell(
  value: string,
  key: string,
  border: string,
  emphasize = false,
  sign: CellSign = "neutral",
  title?: string
): React.ReactNode {
  // Match the main dashboard tabs: green for positive, red for negative,
  // theme default for neutral / actuals view.
  const color =
    sign === "positive"
      ? "#28a745"
      : sign === "negative"
      ? "#dc3545"
      : undefined;
  return (
    <td
      key={key}
      title={title}
      style={{
        padding: "6px 10px",
        textAlign: "right",
        border: `1px solid ${border}`,
        fontVariantNumeric: "tabular-nums",
        fontWeight: emphasize ? 600 : 400,
        color,
      }}
    >
      {value}
    </td>
  );
}

function periodWordFor(alignment: string | undefined): string {
  switch (alignment) {
    case "D":
      return "days";
    case "W":
      return "weeks";
    case "M":
      return "months";
    case "Q":
      return "quarters";
    case "H":
      return "halves";
    case "Y":
      return "years";
    default:
      return "periods";
  }
}

export default HistoricalTrendsTab;
